import { DefaultButton, DefaultEffects, PrimaryButton, Stack, Text } from '@fluentui/react';
import { useTheme } from '@fluentui/react-theme-provider';
import Badge from 'components/Badge';
import DynamicForm from 'components/DynamicForm/DynamicForm';
import { IvicosColors, getTextColorForBackground } from 'kits/colorKit';
import { Memo } from 'kits/IconKit2';
import IvicosStrings from 'kits/language/stringKit';
import React, { useEffect } from 'react';
import { useIsWaitingAreaMemoDialogOpen } from 'shared-state/display-options/hooks';
import { IRoomSelecitonDialogState } from './RoomSelectionDialog.state';
import WaitingAreaMemoComposer from './WaitingAreaMemoComposer/WaitingAreaMemoComposer';

export const RoomSelectionDialogView: React.FC<IRoomSelecitonDialogState> = ({
    branding,
    eventName,
    visitorName,
    formFields,
    onWaitingRoomFormChange,
    letVisitorInside,
    changes,
    selectedVisitor,
    fetchedRooms,
    activeBtn,
    setActiveBtn,
    selectedArea,
    buttonLabelText,
    backToArea
}) => {
    const theme = useTheme();

    const backgroundColor =
        (branding &&
            branding.orgColors &&
            (branding.orgColors.length > 1 ? `linear-gradient(45deg,${branding.orgColors.join(',')})` : branding.orgColors[0])) ||
        IvicosColors.CampusGradient;

    useEffect(() => {
        if (fetchedRooms.length === 0) {
            setActiveBtn(true);
        }
    }, [fetchedRooms]);

    const [isWaitingAreaMemoDialogOpen, setIsWaitingAreaMemoDialogOpen] = useIsWaitingAreaMemoDialogOpen();

    return (
        <>
            <Stack style={{ marginTop: 16, width: 340, boxShadow: DefaultEffects.elevation64 }}>
                <Stack
                    verticalAlign={'center'}
                    style={{ background: backgroundColor, borderRadius: '4px 4px 0px 0px', padding: 20 }}
                    tokens={{ childrenGap: 20 }}
                >
                    <Text
                        variant="xLargePlus"
                        style={{
                            lineHeight: '35px',
                            color: getTextColorForBackground(backgroundColor)
                        }}
                    >
                        {eventName}
                    </Text>
                    <Text
                        aria-label="meet-area-you-are-talking-to"
                        variant="medium"
                        style={{
                            color: getTextColorForBackground(backgroundColor)
                        }}
                    >
                        {IvicosStrings.youAreTalkingTo}
                    </Text>
                    <Stack aria-label="meet-area-visitor-badge">
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <Badge text={visitorName || ''} size="50" fontSize="medium" textColor={backgroundColor} />
                            <Text
                                variant="large"
                                style={{
                                    lineHeight: '25px',
                                    fontWeight: 'bold',
                                    wordBreak: 'break-word',
                                    color: getTextColorForBackground(backgroundColor)
                                }}
                            >
                                {visitorName || ''}
                            </Text>
                        </Stack>
                    </Stack>
                    <Stack style={{ width: '100%' }} horizontal>
                        <DefaultButton
                            text={IvicosStrings.sendMemoToVisitor}
                            onRenderIcon={() => <Memo />}
                            onClick={() => setIsWaitingAreaMemoDialogOpen(true)}
                        />
                    </Stack>
                </Stack>

                <Stack style={{ backgroundColor: 'white', padding: 20 }} tokens={{ childrenGap: 8 }}>
                    <Stack>
                        <Text
                            aria-label="meet-area-direct-visitor-to-room"
                            variant="large"
                            style={{
                                fontWeight: 'bold',
                                color: theme.palette.black
                            }}
                        >
                            {IvicosStrings.directVisitorToRoom}
                        </Text>
                    </Stack>
                    <Stack>
                        <DynamicForm formFields={formFields} onChange={onWaitingRoomFormChange}></DynamicForm>
                        {selectedArea && fetchedRooms.length === 0 && (
                            <Text style={{ fontWeight: 600, color: 'red' }}>{IvicosStrings.NoRoomAvailableDescription}</Text>
                        )}
                    </Stack>

                    {/* FOOTER BUTTONS */}
                    <Stack horizontal style={{ marginTop: 16 }}>
                        {/* <Stack style={{ flexDirection: 'row', padding: '10px', width: '100%', justifyContent: 'space-around' }}> */}
                        <DefaultButton
                            aria-label="back-to-area-button"
                            style={{ marginRight: '10px' }}
                            text={IvicosStrings.backToAreaButton}
                            onClick={backToArea}
                            iconProps={{ iconName: 'Leave' }}
                        />
                        <PrimaryButton
                            aria-label="meet-area-let-them-in-button"
                            text={buttonLabelText()}
                            disabled={activeBtn}
                            onClick={() => letVisitorInside(selectedVisitor || '', changes.room, changes.area.selectedKey)}
                        />
                    </Stack>
                </Stack>
            </Stack>
            {isWaitingAreaMemoDialogOpen && (
                <WaitingAreaMemoComposer
                    recipientName={visitorName || ''}
                    recipientId={selectedVisitor || ''}
                    sentBy="host"
                    isOpen={isWaitingAreaMemoDialogOpen}
                    onClose={() => setIsWaitingAreaMemoDialogOpen(false)}
                />
            )}
        </>
    );
};
